// Product readiness — a product at a branch/station is only usable for daily
// entries when it has BOTH at least one tank and at least one pump. Anything
// less renders a broken-looking entry form, so every surface (dashboards,
// product pickers, entry forms, branch editors, onboarding) gates on this.

export interface ProductReadiness {
  product: string;
  hasTank: boolean;
  hasPump: boolean;
  ready: boolean;
  /** Human fragments of what's missing, e.g. ["a PMS tank"] */
  missing: string[];
}

interface EquipmentHolder {
  tanks?: Array<{ product: string }>;
  pumps?: Array<{ product: string }>;
}

/** Readiness per product present at the branch (union of tank+pump products). */
export function productReadiness(unit: EquipmentHolder): ProductReadiness[] {
  const tanks = unit.tanks ?? [];
  const pumps = unit.pumps ?? [];
  const products = [...new Set([...tanks.map((t) => t.product), ...pumps.map((p) => p.product)])].sort();
  return products.map((product) => {
    const hasTank = tanks.some((t) => t.product === product);
    const hasPump = pumps.some((p) => p.product === product);
    const missing = [
      ...(hasTank ? [] : [`a ${product} tank`]),
      ...(hasPump ? [] : [`a ${product} pump`]),
    ];
    return { product, hasTank, hasPump, ready: hasTank && hasPump, missing };
  });
}

/** Readiness for one product; ready=false with both missing if product absent. */
export function productReady(unit: EquipmentHolder, product: string): ProductReadiness {
  return (
    productReadiness(unit).find((r) => r.product === product) ?? {
      product, hasTank: false, hasPump: false, ready: false,
      missing: [`a ${product} tank`, `a ${product} pump`],
    }
  );
}

/** Products configured one-sided (tank without pump, or pump without tank). */
export function lopsidedProducts(unit: EquipmentHolder): ProductReadiness[] {
  return productReadiness(unit).filter((r) => !r.ready);
}
